import React from "react";
import { Icon, Ring, notify, confirmDialog } from "./registry";
import { GbpPhotoQueue } from "./website/gbpphotos";
/* ============================================================
   Fence Works — Google Business Profile optimizer
   Scores the profile the way the map pack ranks it (server/src/gbpaudit.js):
   Profile 40 · Reviews 30 · Activity 20 · Consistency 10 — then says what to
   fix first, with the tool to fix it one tap away:
   • AI description (≤750, main service + town up front, no links/promos)
   • four weekly "What's new" posts, one-tap post to Google
   • a services list to paste in
   • the review campaign / reply queue for the review section
   Apply to Google (description, website link, services) happens only on an
   explicit press, one field at a time, and every change can be undone.
   ============================================================ */

const GBP_HOME = "https://business.google.com/";
const STATUS: any = {
  pass: { icon: "check", color: "var(--green)", label: "Good" },
  warn: { icon: "bolt", color: "var(--amber)", label: "Improve" },
  fail: { icon: "x", color: "var(--red)", label: "Fix" },
  unknown: { icon: "help", color: "var(--fg-3)", label: "Can't read" },
};
const gradeColor = (g: any) => (g === "A" ? "var(--green)" : g === "B" ? "var(--teal)" : g === "C" ? "var(--amber)" : "var(--red)");
const nf = (n: any) => (n == null ? "—" : Number(n).toLocaleString("en-US"));

function GbpOptimizerPage({ store, go }: any) {
  const DB: any = (window as any).VFP_DB;
  const live = !!(DB && DB.apiBase && DB.apiBase());
  const [a, setA] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [tool, setTool] = React.useState<any>("description");
  const toolsRef = React.useRef<any>(null);
  const settings = store.settings || {};

  const load = React.useCallback(async (fresh?: boolean) => {
    if (!live) { setA({ connected: false, offline: true, setup: [] }); return; }
    setBusy(true); setErr("");
    try { setA(await DB.gbpAudit(fresh)); }
    catch (e: any) { setErr(String(e.message || e)); }
    setBusy(false);
  }, [live]);
  React.useEffect(() => { load(); }, [load]);

  // one-tap Apply to Google: one field, explicit confirm, and Undo afterwards
  const [applied, setApplied] = React.useState<any>(null);   // { field, eventId, pending }
  const applyField = async (field: string, value: any, question: string) => {
    if (!live) { notify("Connect to the Fence Works service to apply changes."); return false; }
    const ok = await confirmDialog({ title: question, body: "Only this one field changes on your Business Profile. Google may review the edit before it shows, and you can undo it here.", confirmLabel: "Apply to Google" });
    if (!ok) return false;
    try {
      const r = await DB.gbpApply(field, value);
      setApplied({ field, eventId: r.eventId, pending: r.pending });
      notify(r.pending ? "Sent to Google — it shows once Google approves the edit." : "Updated on Google.");
      load(true);
      return true;
    } catch (e: any) { notify(e.message || "Google didn't accept the change.", { kind: "error" }); return false; }
  };
  const undoApplied = async () => {
    if (!applied || !applied.eventId) return;
    try { await DB.gbpUndo(applied.eventId); notify("Undone — your previous " + (FIELD_LABEL[applied.field] || "value") + " is back."); setApplied(null); load(true); }
    catch (e: any) { notify(e.message || "Couldn't undo.", { kind: "error" }); }
  };
  const openTool = (t: string) => { setTool(t); setTimeout(() => toolsRef.current && toolsRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 30); };
  const copy = (t: string, what = "Copied") => { try { navigator.clipboard.writeText(t); notify(what + "."); } catch (e) { notify("Copy failed — select it and copy by hand.", { kind: "error" }); } };
  const doAction = (x: any) => {
    switch (x.action) {
      case "description": case "posts": case "services": return openTool(x.action);
      case "campaign": return go("reviews", { tab: "campaigns" });
      case "reviews": return go("reviews");
      case "automation": return go("ai", { tab: "automations" });
      case "website":
        if (x.value && a && a.connected) { applyField("website", x.value, `Point your Google listing at ${x.value}?`); return; }
        if (x.value) { copy(x.value, "Tracked website link copied — paste it into Website in Google"); } window.open(GBP_HOME, "_blank", "noopener"); return;
      default: window.open(GBP_HOME, "_blank", "noopener");
    }
  };
  const useReviewLink = (url: string) => { store.setSettings({ googleReviewUrl: url }); notify("Saved as your Google review link — every review request and QR code now uses it."); setA((x: any) => ({ ...x, suggestReviewLink: null })); };

  return (
    <div className="page fade-up" style={{ maxWidth: 1080 }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="kicker kicker-teal">Reputation</span>
          <h1 className="display" style={{ fontSize: 28, margin: "6px 0 4px" }}>Google Profile</h1>
          <p className="muted" style={{ margin: 0, maxWidth: 640 }}>Your Google Business Profile decides whether you show up in the map pack for "fence company near me". Here's your score, what's holding it back, and the tools to fix it.</p>
        </div>
        <div className="row gap-8" style={{ alignSelf: "flex-start", flexWrap: "wrap" }}>
          {a && a.connected && <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => load(true)}><Icon name="undo" size={14} />{busy ? "Checking…" : "Re-check"}</button>}
          <a className="btn btn-outline btn-sm" href={(a && a.mapsUrl) || GBP_HOME} target="_blank" rel="noopener"><Icon name="arrow" size={14} />Open in Google</a>
        </div>
      </div>

      {err && <div className="card pad" style={{ marginBottom: 16 }}><b>Couldn't read your profile.</b><p className="muted" style={{ fontSize: 13, margin: "4px 0 0" }}>{err}</p></div>}
      {!a && !err && <div className="card pad muted">Reading your Google profile…</div>}

      {a && a.suggestReviewLink && (
        <div className="card pad row between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap", borderColor: "var(--teal-tint-2)" }}>
          <div style={{ maxWidth: 680 }}>
            <b>We found your Google review link</b>
            <p className="muted" style={{ fontSize: 13, margin: "3px 0 0", wordBreak: "break-all" }}>{a.suggestReviewLink}</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => useReviewLink(a.suggestReviewLink)}>Use it everywhere</button>
        </div>
      )}

      {applied && (
        <div className="card pad row between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap", borderColor: "var(--green-tint)" }}>
          <div><b>{FIELD_LABEL[applied.field] ? FIELD_LABEL[applied.field][0].toUpperCase() + FIELD_LABEL[applied.field].slice(1) : "Change"} applied to Google</b>
            <p className="muted" style={{ fontSize: 13, margin: "3px 0 0" }}>{applied.pending ? "Google is reviewing the edit — it usually shows within a few hours." : "It's live on your profile."}</p></div>
          {applied.eventId && <button className="btn btn-ghost btn-sm" onClick={undoApplied}><Icon name="undo" size={14} />Undo</button>}
        </div>
      )}
      {a && !a.connected && <NotConnected a={a} go={go} settings={settings} />}
      {a && a.connected && <Report a={a} doAction={doAction} />}
      {/* job photos → the profile: approve / skip, auto-post (website/gbpphotos.tsx, server/src/sites/gbpphotos.js) */}
      {a && a.connected && <GbpPhotoQueue live={live} />}

      <div ref={toolsRef} />
      {a && <AiTools tool={tool} setTool={setTool} connected={!!(a && a.connected)} live={live} current={a && a.profile} copy={copy} applyField={applyField} />}
    </div>
  );
}

function NotConnected({ a, go, settings }: any) {
  return (
    <div className="card pad" style={{ marginBottom: 16 }}>
      <div className="row gap-16" style={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <Ring value={0} size={96}><span className="display" style={{ fontSize: 24 }}>?</span></Ring>
        <div style={{ flex: "1 1 360px" }}>
          <h2 className="display" style={{ fontSize: 20, margin: "0 0 6px" }}>Connect Google to get your score</h2>
          <p className="muted" style={{ fontSize: 13.5, margin: "0 0 12px" }}>We read your profile, reviews, posts, photos and the last 28 days of calls and clicks, score it out of 100, and line up the fixes that move you up the map pack. Reading never changes your profile.</p>
          <div className="gbp-setup">
            {(a.setup || []).map((s: any) => (
              <button key={s.id} className="gbp-setup-row" onClick={() => go(s.action === "integrations" ? "integrations" : "settings", s.action === "settings" ? { section: "company" } : undefined)}>
                <span className="gbp-dot" style={{ background: s.done ? "var(--green)" : "var(--line-2)" }}>{s.done && <Icon name="check" size={11} />}</span>
                <span><b>{s.label}</b><span className="faint"> — {s.detail}</span></span>
                <Icon name="chevright" size={14} />
              </button>
            ))}
            {a.offline && <p className="faint" style={{ fontSize: 12.5 }}>Connect to the Fence Works service to run the audit.</p>}
          </div>
          {!a.offline && <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => go("integrations")}><Icon name="bolt" size={14} />Connect Google Business Profile</button>}
          {!settings.googleReviewUrl && <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>Tip: once connected we'll find your Google review link for you.</p>}
        </div>
      </div>
    </div>
  );
}

function Report({ a, doAction }: any) {
  const [openSec, setOpenSec] = React.useState<any>(null);
  const p = a.performance;
  return (
    <>
      <div className="gbp-top">
        <div className="card pad gbp-score">
          <Ring value={a.score || 0} size={120} stroke={10} color={gradeColor(a.grade)}>
            <div style={{ textAlign: "center" }}>
              <div className="display tnum" style={{ fontSize: 34 }}>{a.score != null ? a.score : "—"}</div>
              <div className="faint" style={{ fontSize: 11 }}>of 100</div>
            </div>
          </Ring>
          <div>
            <span className="chip" style={{ color: gradeColor(a.grade), borderColor: "currentColor" }}>Grade {a.grade || "—"}</span>
            <div className="gbp-secs">
              {(a.sections || []).map((s: any) => (
                <button key={s.id} className="gbp-sec" onClick={() => setOpenSec(openSec === s.id ? null : s.id)}>
                  <span>{s.label}</span>
                  <i><em style={{ width: (s.max ? Math.round((100 * s.points) / s.max) : 0) + "%", background: s.max && s.points / s.max >= 0.8 ? "var(--green)" : s.max && s.points / s.max >= 0.5 ? "var(--amber)" : "var(--red)" }} /></i>
                  <b className="tnum">{s.points}/{s.max}</b>
                </button>
              ))}
            </div>
            {a.cached && <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Checked a few minutes ago · Re-check for fresh numbers</p>}
          </div>
        </div>
        <div className="card pad">
          <div className="kicker" style={{ marginBottom: 10 }}>Last 28 days on Google{a.window ? <span className="faint" style={{ textTransform: "none", letterSpacing: 0 }}> · {a.window.start} → {a.window.end}</span> : null}</div>
          {p ? (
            <div className="gbp-perf">
              <div><b className="display tnum">{nf(p.calls)}</b><span>calls</span></div>
              <div><b className="display tnum">{nf(p.websiteClicks)}</b><span>website clicks</span></div>
              <div><b className="display tnum">{nf(p.directions)}</b><span>direction requests</span></div>
              <div><b className="display tnum">{nf(p.searchViews)}</b><span>views in Search</span></div>
              <div><b className="display tnum">{nf(p.mapsViews)}</b><span>views in Maps</span></div>
            </div>
          ) : <p className="muted" style={{ fontSize: 13 }}>Google didn't return performance numbers{a.errors && a.errors.performance ? ` (${a.errors.performance})` : ""}. The Business Profile Performance API must be enabled on the Google Cloud project.</p>}
          {a.keywords && a.keywords.length > 0 && (
            <>
              <div className="kicker" style={{ margin: "16px 0 8px" }}>What people searched to find you · last 3 months</div>
              <div className="gbp-kw">
                {a.keywords.map((k: any) => <span key={k.keyword}><b>{k.keyword}</b><em className="tnum">{k.impressions != null ? nf(k.impressions) : "<" + (k.threshold || 15)}</em></span>)}
              </div>
            </>
          )}
        </div>
      </div>

      {a.actions && a.actions.length > 0 && (
        <div className="card pad" style={{ marginBottom: 16 }}>
          <div className="kicker kicker-teal" style={{ marginBottom: 10 }}>Biggest wins first</div>
          <div style={{ display: "grid", gap: 8 }}>
            {a.actions.map((x: any, i: number) => (
              <div key={x.id} className="gbp-action">
                <span className="gbp-n">{i + 1}</span>
                <div style={{ flex: 1, minWidth: 0 }}><b>{x.label}</b><div className="muted" style={{ fontSize: 13 }}>{x.text}</div></div>
                <span className="chip teal tnum" title="Points this fix adds">+{x.gain}</span>
                <button className="btn btn-outline btn-sm" onClick={() => doAction(x)}>{ACTION_LABEL[x.action] || "Fix in Google"}<Icon name="chevright" size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="kicker" style={{ marginBottom: 10 }}>Every check</div>
        {(a.sections || []).map((s: any) => (
          <details key={s.id} open={openSec === s.id || undefined} className="gbp-details">
            <summary><b>{s.label}</b><span className="faint tnum"> · {s.points}/{s.max}</span></summary>
            {s.checks.map((c: any) => {
              const st = STATUS[c.status] || STATUS.unknown;
              return (
                <div key={c.id} className="gbp-check">
                  <span className="gbp-dot" style={{ background: st.color }}><Icon name={st.icon} size={11} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{c.label}</b> <span className="faint tnum">{c.status === "unknown" ? "" : `${c.points}/${c.max}`}</span>
                    <div className="muted" style={{ fontSize: 13 }}>{c.detail}</div>
                    {c.fix && <div style={{ fontSize: 12.5, marginTop: 3 }}>{c.fix.text}</div>}
                  </div>
                  {c.fix && c.status !== "pass" && <button className="rv-link" onClick={() => doAction({ ...c.fix, id: c.id })}>{ACTION_LABEL[c.fix.action] || "Fix"}</button>}
                </div>
              );
            })}
          </details>
        ))}
      </div>
    </>
  );
}
const ACTION_LABEL: any = { description: "Write it", posts: "Write posts", services: "Build the list", campaign: "Ask past customers", reviews: "Reply now", automation: "Turn on", website: "Fix the link", photos: "Add photos", open_gbp: "Open Google" };
const FIELD_LABEL: any = { description: "description", website: "website link", services: "services list" };

function AiTools({ tool, setTool, connected, live, current, copy, applyField }: any) {
  const DB: any = (window as any).VFP_DB;
  const [diff, setDiff] = React.useState<any>("");
  const [desc, setDesc] = React.useState<any>("");
  const [posts, setPosts] = React.useState<any>([]);
  const [services, setServices] = React.useState<any>([]);
  const [busy, setBusy] = React.useState<any>("");

  const run = async (kind: string) => {
    if (!live) { notify("Connect to the Fence Works service to use the AI tools."); return; }
    setBusy(kind);
    try {
      const r = await DB.gbpSuggest(kind, diff);
      if (kind === "description") setDesc(r.description || "");
      if (kind === "posts") setPosts(r.posts || []);
      if (kind === "services") setServices(r.services || []);
    } catch (e: any) { notify(e.message || "The AI couldn't write that — try again.", { kind: "error" }); }
    setBusy("");
  };
  const postNow = async (p: any, i: number) => {
    const text = (p.title ? p.title + "\n\n" : "") + p.body;
    if (!(await confirmDialog({ title: "Post this to Google now?", body: "It appears on your Business Profile as a \"What's new\" update with a link to your website.", confirmLabel: "Post to Google" }))) return;
    setBusy("post" + i);
    try { await DB.gbpPost(text); notify("Posted to your Google profile."); setPosts((ps: any[]) => ps.map((x, j) => (j === i ? { ...x, posted: true } : x))); }
    catch (e: any) { notify(e.message || "Google didn't take the post.", { kind: "error" }); }
    setBusy("");
  };

  return (
    <div className="card pad">
      <div className="row between" style={{ gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <span className="kicker kicker-teal">AI profile tools</span>
          <h3 className="display" style={{ fontSize: 18, margin: "6px 0 0" }}>Write it once, rank for years</h3>
        </div>
        <div className="cat-tabs" style={{ margin: 0 }}>
          {[["description", "Description"], ["posts", "Weekly posts"], ["services", "Services list"]].map(([id, lb]) => (
            <button key={id} className={"filter-chip" + (tool === id ? " on" : "")} onClick={() => setTool(id)}>{lb}</button>
          ))}
        </div>
      </div>
      <label className="field-label">What makes you different? <span className="faint">(optional — the AI only uses facts you give it)</span></label>
      <textarea className="textarea" rows={2} value={diff} placeholder="e.g. family-owned since 2012, steel posts set below the frost line, 10-year workmanship warranty, we pull every permit" onChange={(e: any) => setDiff(e.target.value)} />

      {tool === "description" && (
        <div style={{ marginTop: 14 }}>
          {current && current.description && !desc && (
            <div className="gbp-current"><span className="kicker">Now on Google · {current.description.length}/750</span><p>{current.description}</p></div>
          )}
          <button className="btn btn-primary btn-sm" disabled={busy === "description"} onClick={() => run("description")}><Icon name="bolt" size={14} />{busy === "description" ? "Writing…" : desc ? "Write another" : "Write my description"}</button>
          <span className="faint" style={{ fontSize: 11.5, marginLeft: 10 }}>1 AI credit</span>
          {desc && (
            <div style={{ marginTop: 12 }}>
              <textarea className="textarea" rows={7} value={desc} onChange={(e: any) => setDesc(e.target.value.slice(0, 750))} />
              <div className="row between" style={{ marginTop: 6, flexWrap: "wrap", gap: 8 }}>
                <span className="faint tnum" style={{ fontSize: 12, color: desc.length > 750 ? "var(--red)" : undefined }}>{desc.length}/750 characters</span>
                <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => copy(desc, "Description copied")}>Copy</button>
                  {connected
                    ? <button className="btn btn-primary btn-sm" disabled={desc.length > 750 || desc.length < 80} onClick={() => applyField("description", desc, "Replace your Google business description with this one?")}><Icon name="check" size={13} />Apply to Google</button>
                    : <a className="btn btn-outline btn-sm" href={GBP_HOME} target="_blank" rel="noopener" onClick={() => copy(desc, "Description copied — paste it under Edit profile → Description")}>Copy & open Google</a>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tool === "posts" && (
        <div style={{ marginTop: 14 }}>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}>Profiles that post weekly look active to Google and to customers. Four posts, one a week — a seasonal tip, a service spotlight, how you build, and a common question.</p>
          <button className="btn btn-primary btn-sm" disabled={busy === "posts"} onClick={() => run("posts")}><Icon name="bolt" size={14} />{busy === "posts" ? "Writing…" : posts.length ? "Write four more" : "Write this month's posts"}</button>
          <span className="faint" style={{ fontSize: 11.5, marginLeft: 10 }}>1 AI credit</span>
          <div className="gbp-posts">
            {posts.map((p: any, i: number) => (
              <div key={i} className="gbp-post">
                <span className="faint" style={{ fontSize: 11 }}>Week {i + 1}</span>
                {p.title && <b>{p.title}</b>}
                <textarea className="textarea" rows={6} value={p.body} onChange={(e: any) => setPosts((ps: any[]) => ps.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} />
                <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => copy((p.title ? p.title + "\n\n" : "") + p.body, "Post copied")}>Copy</button>
                  {connected && (p.posted ? <span className="chip green">Posted</span> : <button className="btn btn-outline btn-sm" disabled={busy === "post" + i} onClick={() => postNow(p, i)}><Icon name="send" size={13} />{busy === "post" + i ? "Posting…" : "Post to Google now"}</button>)}
                </div>
              </div>
            ))}
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Want these to go out on their own? Turn on the social posting automation — it posts finished jobs with photos after each job, with your approval.</p>
        </div>
      )}

      {tool === "services" && (
        <div style={{ marginTop: 14 }}>
          {current && current.services && current.services.length > 0 && <p className="faint" style={{ fontSize: 12.5 }}>Now on Google: {current.services.join(", ")}</p>}
          <button className="btn btn-primary btn-sm" disabled={busy === "services"} onClick={() => run("services")}><Icon name="bolt" size={14} />{busy === "services" ? "Writing…" : "Build my services list"}</button>
          <span className="faint" style={{ fontSize: 11.5, marginLeft: 10 }}>1 AI credit</span>
          {services.length > 0 && (
            <>
              <div className="gbp-services">
                {services.map((s: any, i: number) => <div key={i}><b>{s.name}</b><span className="muted">{s.description}</span></div>)}
              </div>
              <div className="row gap-8" style={{ marginTop: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => copy(services.map((s: any) => `${s.name}\n${s.description}`).join("\n\n"), "Services copied")}>Copy all</button>
                {connected
                  ? <button className="btn btn-primary btn-sm" onClick={() => applyField("services", services, `Add ${services.length} services to your Google profile?`)}><Icon name="check" size={13} />Add to Google</button>
                  : <a className="btn btn-outline btn-sm" href={GBP_HOME} target="_blank" rel="noopener">Open Google → Edit services</a>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

Object.assign(window, { GbpOptimizerPage });
export { GbpOptimizerPage };
